<template>
  <div class="min-vh-100 bg-light d-flex flex-column">

    <!-- Header -->
    <div class="bg-dark text-white text-center py-3 px-3">
      <div class="h5 font-weight-bold mb-0">{{ qrInfo.company_name || 'Check in' }}</div>
      <small v-if="qrInfo.label" class="text-white-50">{{ qrInfo.label }}</small>
    </div>

    <div class="flex-grow-1 px-3 pt-4 pb-5">
      <div class="mx-auto w-100" style="max-width:420px;">

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
        </div>

        <div v-else-if="fatalError" class="alert alert-danger text-center">
          <i class="bi bi-exclamation-triangle mr-1"></i>{{ fatalError }}
        </div>

        <template v-else>

          <!-- ── REMEMBERED MEMBER: one tap ── -->
          <div v-if="view === 'member'" class="card border-0 shadow-sm">
            <div class="card-body p-4 text-center">
              <div class="text-secondary small mb-1">Welcome back</div>
              <h4 class="font-weight-bold text-dark mb-3">{{ member.name }}</h4>

              <div v-if="member.subscription" class="mb-4">
                <span class="badge px-3 py-2" :class="subActive ? 'badge-primary' : 'badge-danger'">
                  {{ member.subscription.package }} ·
                  <template v-if="subActive">{{ daysLeftLabel }}</template>
                  <template v-else>Expired</template>
                </span>
              </div>

              <button class="btn btn-primary btn-lg btn-block py-3" :disabled="busy" @click="checkIn">
                <span v-if="busy" class="spinner-border spinner-border-sm mr-2"></span>
                <i v-else class="bi bi-box-arrow-in-right mr-2"></i>Check in
              </button>

              <div class="d-flex justify-content-between mt-3">
                <a :href="portalUrl" class="text-primary small"><i class="bi bi-person-badge mr-1"></i>My membership</a>
                <a href="#" class="text-secondary small" @click.prevent="notMe">Not {{ firstName }}?</a>
              </div>
            </div>
          </div>

          <!-- ── PHONE ── -->
          <div v-else-if="view === 'phone'" class="card border-0 shadow-sm">
            <div class="card-body p-4">
              <h5 class="font-weight-bold text-dark text-center mb-1">Check in or join</h5>
              <p class="text-secondary text-center small mb-4">Enter your phone number. We'll remember this phone next time.</p>
              <vue-tel-input v-model="phone" class="mb-3" @enter="identify"></vue-tel-input>
              <button class="btn btn-primary btn-lg btn-block" :disabled="busy || !phone" @click="identify">
                <span v-if="busy" class="spinner-border spinner-border-sm mr-2"></span>
                Continue
              </button>
            </div>
          </div>

          <!-- ── OTP ── -->
          <div v-else-if="view === 'otp'" class="card border-0 shadow-sm">
            <div class="card-body p-4 text-center">
              <h5 class="font-weight-bold text-dark mb-1">Enter your code</h5>
              <p class="text-secondary small mb-4">We sent a 4-digit code to {{ maskedPhone }}. You only need this once on this phone.</p>
              <input
                v-model="otp"
                type="text"
                inputmode="numeric"
                maxlength="6"
                autocomplete="one-time-code"
                class="form-control form-control-lg text-center mb-3"
                placeholder="••••"
                @keyup.enter="verify"
              />
              <button class="btn btn-primary btn-lg btn-block" :disabled="busy || otp.length < 4" @click="verify">
                <span v-if="busy" class="spinner-border spinner-border-sm mr-2"></span>
                Verify
              </button>
              <button class="btn btn-link text-secondary mt-2" @click="toPhone">Use a different number</button>
            </div>
          </div>

          <!-- ── SIGN UP ── -->
          <div v-else-if="view === 'signup'" class="card border-0 shadow-sm">
            <div class="card-body p-4">
              <h5 class="font-weight-bold text-dark mb-1">Join {{ qrInfo.company_name }}</h5>
              <p class="text-secondary small mb-4">Takes 30 seconds. No account or password needed.</p>
              <div class="form-group">
                <label class="text-secondary small mb-1">Full name</label>
                <input v-model="signupForm.full_name" type="text" class="form-control form-control-lg" autocomplete="name" />
              </div>
              <div class="form-group">
                <label class="text-secondary small mb-1">Phone</label>
                <vue-tel-input v-model="phone"></vue-tel-input>
              </div>
              <div class="form-group">
                <label class="text-secondary small mb-1">Email (optional)</label>
                <input v-model="signupForm.email" type="email" class="form-control form-control-lg" autocomplete="email" />
              </div>
              <button class="btn btn-primary btn-lg btn-block" :disabled="busy || !signupForm.full_name.trim() || !phone" @click="signup">
                <span v-if="busy" class="spinner-border spinner-border-sm mr-2"></span>
                Continue
              </button>
              <button class="btn btn-link btn-block text-secondary" @click="toPhone">Back</button>
            </div>
          </div>

          <!-- ── CHOOSE PACKAGE (new member / no subscription) ── -->
          <div v-else-if="view === 'join'" class="card border-0 shadow-sm">
            <div class="card-body p-4">
              <h5 class="font-weight-bold text-dark mb-1">Choose a plan</h5>
              <p class="text-secondary small mb-3">{{ joinMessage || 'Pick a plan to start today.' }}</p>

              <div class="list-group mb-3">
                <button
                  v-for="p in packages"
                  :key="p.id"
                  type="button"
                  class="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                  :class="{ active: selectedPackage && selectedPackage.id === p.id }"
                  @click="selectedPackage = p"
                >
                  <span class="text-left">
                    <span class="d-block font-weight-bold">{{ p.name }}</span>
                    <small :class="selectedPackage && selectedPackage.id === p.id ? 'text-white-50' : 'text-secondary'">
                      {{ durationLabel(p.duration_value, p.duration_unit) }}
                    </small>
                  </span>
                  <span class="font-weight-bold">{{ money(p.price) }}</span>
                </button>
                <div v-if="!packages.length" class="list-group-item text-secondary text-center">
                  No plans are available online. Please ask at the front desk.
                </div>
              </div>

              <member-pay-panel
                v-if="selectedPackage"
                :key="'sub-' + selectedPackage.id"
                :member-token="memberToken"
                :intent="{ type: 'subscribe', package_id: selectedPackage.id }"
                :amount="selectedPackage.price"
                :wallet="member.wallet || 0"
                :can-pay-online="canPayOnline"
                :default-phone="member.phone || ''"
                @done="afterPayment"
              />
            </div>
          </div>

          <!-- ── RENEW ── -->
          <div v-else-if="view === 'renew'" class="card border-0 shadow-sm">
            <div class="card-body p-4">
              <div class="text-center mb-3">
                <i class="bi bi-calendar-x text-danger" style="font-size:2.5rem;"></i>
                <h5 class="font-weight-bold text-dark mt-2 mb-1">Your membership has expired</h5>
                <p class="text-secondary small mb-0">
                  {{ member.subscription.package }} · ended {{ member.subscription.end_date }}
                </p>
              </div>
              <member-pay-panel
                :member-token="memberToken"
                :intent="{ type: 'renew', subscription_id: member.subscription.id }"
                :amount="member.subscription.renew_price"
                :wallet="member.wallet || 0"
                :can-pay-online="canPayOnline"
                :default-phone="member.phone || ''"
                @done="afterPayment"
              />
              <button class="btn btn-link btn-block text-secondary mt-2" @click="openJoin('')">Choose a different plan</button>
            </div>
          </div>

          <!-- ── CHECKED IN ── -->
          <div v-else-if="view === 'done'" class="card border-0 shadow-sm">
            <div class="card-body p-4 text-center">
              <i class="bi bi-check-circle-fill text-primary" style="font-size:3.5rem;"></i>
              <h4 class="font-weight-bold text-dark mt-2 mb-1">You're in!</h4>
              <p class="text-secondary mb-3">{{ result.message }}</p>
              <div class="d-flex justify-content-around border-top border-bottom py-3 mb-3">
                <div>
                  <div class="text-secondary small">Time</div>
                  <div class="font-weight-bold text-dark">{{ result.entered_at }}</div>
                </div>
                <div v-if="member.subscription && member.subscription.days_left !== null">
                  <div class="text-secondary small">Days left</div>
                  <div class="font-weight-bold text-dark">{{ member.subscription.days_left }}</div>
                </div>
                <div v-if="result.amount_deducted">
                  <div class="text-secondary small">Charged</div>
                  <div class="font-weight-bold text-dark">{{ money(result.amount_deducted) }}</div>
                </div>
              </div>

              <!-- Install prompt -->
              <div class="bg-light rounded p-3 text-left">
                <div class="font-weight-bold text-dark mb-1"><i class="bi bi-phone mr-1"></i>Get the {{ qrInfo.company_name }} app</div>
                <div class="text-secondary small mb-2">Your pass, renewals and visits in one tap - no app store needed.</div>
                <a :href="portalUrl + '?install=1'" class="btn btn-dark btn-block">Add to home screen</a>
              </div>
            </div>
          </div>

          <!-- ── MESSAGE (already inside / other) ── -->
          <div v-else-if="view === 'message'" class="card border-0 shadow-sm">
            <div class="card-body p-4 text-center">
              <i class="bi bi-info-circle text-secondary" style="font-size:2.5rem;"></i>
              <p class="text-dark mt-2">{{ message }}</p>
              <button class="btn btn-outline-secondary btn-block" @click="view = memberToken ? 'member' : 'phone'">Back</button>
            </div>
          </div>

          <div v-if="error" class="alert alert-danger mt-3">{{ error }}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import MemberPayPanel from './member/MemberPayPanel.vue'
import { getRememberedToken, rememberToken, forgetToken, durationLabel, money } from './member/memberStore'

/**
 * Wall-QR check-in page. First scan = identify by phone (optionally with a
 * one-time SMS code, per gym setting) or sign up; the phone is then
 * remembered so every later scan is a single "Check in" tap. Expired members
 * and new members can pay with M-Pesa right here.
 */
export default {
  name: 'SubscriptionCheckinPage',

  components: { MemberPayPanel },

  props: {
    token: { type: String, required: true },
  },

  data() {
    return {
      loading:         true,
      fatalError:      null,
      qrInfo:          {},
      view:            'phone', // member | phone | otp | signup | join | renew | done | message
      busy:            false,
      error:           '',

      phone:           '',
      maskedPhone:     '',
      otp:             '',
      signupForm:      { full_name: '', email: '' },

      memberToken:     null,
      member:          {},
      result:          {},
      message:         '',

      packages:        [],
      canPayOnline:    false,
      selectedPackage: null,
      joinMessage:     '',
    }
  },

  computed: {
    subActive() {
      const s = this.member.subscription
      return s && s.status === 'active' && s.days_left !== null && s.days_left >= 0
    },
    daysLeftLabel() {
      const d = this.member.subscription.days_left
      return d === 0 ? 'last day today' : `${d} day${d === 1 ? '' : 's'} left`
    },
    firstName() {
      return (this.member.name || '').split(' ')[0] || 'you'
    },
    portalUrl() {
      return this.memberToken ? `/m/${this.memberToken}` : '#'
    },
  },

  async mounted() {
    try {
      const { data } = await axios.get(`/api/checkin/${this.token}`)
      this.qrInfo = data
    } catch (e) {
      this.fatalError = e.response?.data?.message || 'This check-in point is unavailable.'
      this.loading = false
      return
    }

    const remembered = getRememberedToken(this.qrInfo.company_id)
    if (remembered) {
      try {
        const { data } = await axios.post(`/api/checkin/${this.token}/member-status`, { member_token: remembered })
        this.setMember(data)
      } catch (e) {
        forgetToken(this.qrInfo.company_id)
      }
    }

    if (!this.memberToken && new URLSearchParams(window.location.search).get('register') === '1') {
      this.view = 'signup'
    }
    this.loading = false
  },

  methods: {
    durationLabel,
    money,

    setMember(data) {
      this.memberToken = data.member_token
      this.member      = data.member || {}
      rememberToken(this.qrInfo.company_id, this.memberToken)
      this.view = 'member'
    },

    async run(fn) {
      this.error = ''
      this.busy  = true
      try {
        await fn()
      } catch (e) {
        this.error = e.response?.data?.message || 'Something went wrong. Please try again.'
      } finally {
        this.busy = false
      }
    },

    handleIdentity(data) {
      if (data.status === 'not_found') {
        this.view = 'signup'
        return
      }
      if (data.status === 'otp_sent') {
        this.maskedPhone = data.masked_phone
        this.otp  = ''
        this.view = 'otp'
        return
      }
      this.setMember(data)
      // First time on this phone: go straight on to check-in / pay.
      this.checkIn()
    },

    identify() {
      return this.run(async () => {
        const { data } = await axios.post(`/api/checkin/${this.token}/identify`, { phone: this.phone })
        this.handleIdentity(data)
      })
    },

    verify() {
      return this.run(async () => {
        const { data } = await axios.post(`/api/checkin/${this.token}/verify`, { phone: this.phone, code: this.otp })
        this.handleIdentity(data)
      })
    },

    signup() {
      return this.run(async () => {
        const { data } = await axios.post(`/api/checkin/${this.token}/signup`, {
          full_name: this.signupForm.full_name,
          email:     this.signupForm.email || null,
          phone:     this.phone,
        })
        this.handleIdentity(data)
      })
    },

    checkIn() {
      return this.run(async () => {
        const { data } = await axios.post(`/api/checkin/${this.token}/member-checkin`, { member_token: this.memberToken })
        if (data.member) this.member = data.member

        if (data.status === 'checked_in') {
          this.result = data
          this.view   = 'done'
          return
        }
        if (data.status === 'ineligible') {
          const sub = this.member.subscription
          if (sub && sub.id && sub.status !== 'cancelled' && sub.renew_price) {
            this.view = 'renew'
            await this.loadPackages()
          } else {
            await this.openJoin(data.message)
          }
          return
        }
        this.message = data.message || 'You are already checked in.'
        this.view    = 'message'
      })
    },

    async loadPackages() {
      const { data } = await axios.get(`/api/checkin/${this.token}/packages`)
      this.packages     = data.packages
      this.canPayOnline = data.can_pay_online
    },

    async openJoin(message) {
      this.joinMessage     = message && !/subscri/i.test(message) ? message : ''
      this.selectedPackage = null
      await this.loadPackages()
      if (this.packages.length === 1) this.selectedPackage = this.packages[0]
      this.view = 'join'
    },

    afterPayment() {
      // Paid (or wallet covered it) - check straight in.
      this.checkIn()
    },

    notMe() {
      forgetToken(this.qrInfo.company_id)
      this.memberToken = null
      this.member      = {}
      this.toPhone()
    },

    toPhone() {
      this.error = ''
      this.view  = 'phone'
    },
  },
}
</script>
